import type { UseQueryOptions } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { secondsToMilliseconds } from "date-fns";
import invariant from "invariant";
import { range } from "lodash";
import type { StrictOmit } from "ts-essentials";
import { getClients } from "../domain/datastores";
import type {
  ListLogsRequest,
  Log,
  LogFetchResponse,
  LogListResponse,
} from "../services/datastore";
import type { Maybe, ResolvedKeyFactory } from "../types";
import { mergeEnabledOption } from "./utils";

export type PreviewImageResult =
  | { success: true; blob: Blob | null }
  | { success: false; blob: null };

export interface PreviewImageUpload {
  image: Blob;
  index: number;
}

export function useLogKeys() {
  const factory = {
    all: ["logs"] as const,
    lists: () => [...factory.all, "list"] as const,
    list: (request: ListLogsRequest) => [...factory.lists(), request] as const,
    details: () => [...factory.all, "details"] as const,
    detail: (logId: Maybe<Log["id"]>) => [...factory.details(), logId] as const,
    previewImages: (logId: Maybe<Log["id"]>) =>
      [...factory.all, "preview-images", logId] as const,
  } as const;

  return factory;
}

export type LogKeys = ResolvedKeyFactory<typeof useLogKeys>;

export function useLogs<TData = LogListResponse>(
  request: ListLogsRequest,
  options?: UseQueryOptions<LogListResponse, unknown, TData, LogKeys["list"]>
) {
  return useQuery({
    queryKey: useLogKeys().list(request),
    queryFn(context) {
      const { logApi } = getClients();

      return logApi.listLogs(request, context);
    },
    ...options,
  });
}

export function useLog<TData = LogFetchResponse>(
  logId: Maybe<Log["id"]>,
  options?: StrictOmit<
    UseQueryOptions<LogFetchResponse, unknown, TData, LogKeys["detail"]>,
    "initialData"
  >
) {
  const queryClient = useQueryClient();

  const logKeys = useLogKeys();

  return useQuery({
    queryKey: logKeys.detail(logId),
    queryFn(context) {
      invariant(logId != null, "Log ID must be defined");

      const { logApi } = getClients();

      return logApi.getLog({ logId }, context);
    },
    ...options,
    enabled: logId != null && (options?.enabled ?? true),
    initialData() {
      // There could be multiple log list queries in the cache if the
      // user was doing a lot of searching before doing going to a page
      // that uses this hook. The log of interest *might* be in some of
      // those search queries, meaning we need to search through all
      // of them looking for the log of interest
      const listQueries = queryClient.getQueriesData<LogListResponse>({
        queryKey: logKeys.lists(),
        predicate: (query) => query.state.status === "success",
      });

      for (const [, query] of listQueries) {
        if (query === undefined) {
          continue;
        }

        for (const log of query.data) {
          if (log.id === logId) {
            return { data: log };
          }
        }
      }
    },
  });
}

export function usePreviewImages<TData = PreviewImageResult[]>(
  logId: Maybe<Log["id"]>,
  numThumbnails: number,
  options?: StrictOmit<
    UseQueryOptions<
      PreviewImageResult[],
      unknown,
      TData,
      LogKeys["previewImages"]
    >,
    "staleTime" | "cacheTime"
  >
) {
  return useQuery({
    queryKey: useLogKeys().previewImages(logId),
    async queryFn() {
      invariant(logId != null, "Log ID must be defined");

      const { logApi } = getClients();

      // Creating a pre-signed URL is essentially an idempotent request
      // so in this specific situation - getting pre-signed URLs to fetch
      // images - it's safe and advantageous to use a query over a mutation
      const presignedUrlResponses = await Promise.all(
        range(numThumbnails).map((index) =>
          logApi.createLogPresignedUrl({
            logId,
            createPresignedURLRequest: {
              method: "get_object",
              params: {
                key: `preview_${index}.webp`,
              },
            },
          })
        )
      );

      const fetchedImagePromises = await Promise.allSettled(
        presignedUrlResponses.map(({ url }) => fetchImageBlob(url))
      );

      const previews: PreviewImageResult[] = fetchedImagePromises.map(
        (promiseResult) => {
          if (promiseResult.status === "fulfilled") {
            return { success: true, blob: promiseResult.value };
          }

          return { success: false, blob: null };
        }
      );

      return previews;
    },
    ...options,
    staleTime: Infinity,
    cacheTime: secondsToMilliseconds(20),
    enabled: mergeEnabledOption(options, logId != null),
  });
}

export function useUploadPreviewImage(logId: Log["id"]) {
  const logKeys = useLogKeys();

  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn({ image, index }: PreviewImageUpload) {
      const { logApi } = getClients();

      const presignedUploadUrl = await logApi.createLogPresignedUrl({
        logId,
        createPresignedURLRequest: {
          method: "put_object",
          params: {
            key: `preview_${index}.webp`,
          },
        },
      });

      const uploadResponse = await fetch(presignedUploadUrl.url, {
        method: "PUT",
        body: image,
      });

      if (!uploadResponse.ok) {
        throw uploadResponse;
      }
    },
    onSuccess() {
      return queryClient.invalidateQueries(logKeys.previewImages(logId));
    },
  });
}

// Utilities

async function fetchImageBlob(url: string): Promise<Blob | null> {
  const response = await fetch(url);

  if (response.ok) {
    return response.blob();
  }

  if (response.status === 404) {
    // In this context, a 404 just means there was no preview image
    // at the given index, so it shouldn't be treated as a fetching error
    // even though there's no Blob to show
    return null;
  }

  throw response;
}
